import { useMemo, useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import {
  Plus,
  Box,
  Search,
  Loader2,
  ChevronLeft,
  ChevronRight,
  Trash2,
  MoreHorizontal,
  Building,
  Car,
} from 'lucide-react';
import { toast } from 'sonner';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { KPI } from '@/components/ui/kpi';
import { Pill } from '@/components/ui/pill';
import { Eyebrow } from '@/components/ui/eyebrow';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@/components/ui/select';
import { useAuth } from '@/lib/auth';
import {
  useBens,
  useBensAggregate,
  useDeleteBem,
  type Bem,
  type BemTipo,
} from '@/lib/hooks/bens';
import { BemDrawer } from '@/features/bens/BemDrawer';
import { fmtBRL, fmtDate } from '@/lib/utils';

export const Route = createFileRoute('/_app/lancamentos/bens')({
  component: BensPage,
});

const PAGE_SIZE = 25;

const TIPO_LABEL: Record<BemTipo, string> = {
  MOVEL: 'móvel',
  IMOVEL: 'imóvel',
  VEICULO: 'veículo',
};

function BensPage() {
  const { user } = useAuth();
  const workspaceId = user?.effectiveWorkspaceId ?? null;

  const [tipo, setTipo] = useState<BemTipo | 'ALL'>('ALL');
  const [search, setSearch] = useState('');
  const [offset, setOffset] = useState(0);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [openRow, setOpenRow] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<Bem | null>(null);

  const filters = useMemo(
    () => ({
      tipo: tipo === 'ALL' ? undefined : tipo,
      search: search.trim() || undefined,
      limit: PAGE_SIZE,
      offset,
    }),
    [tipo, search, offset],
  );

  const listQ = useBens(workspaceId, filters);
  const aggQ = useBensAggregate(workspaceId);
  const delMut = useDeleteBem(workspaceId);

  const items = listQ.data?.items ?? [];
  const total = listQ.data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const currentPage = Math.floor(offset / PAGE_SIZE) + 1;
  const canPrev = offset > 0;
  const canNext = offset + PAGE_SIZE < total;

  async function handleDelete() {
    if (!confirmDelete) return;
    try {
      await delMut.mutateAsync(confirmDelete.id);
      toast.success('Bem removido.');
      setConfirmDelete(null);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Falha ao remover.');
    }
  }

  if (!workspaceId) {
    return (
      <Card variant="default" className="p-8 text-center">
        <p className="text-sm text-fg-muted">Selecione um workspace para gerenciar bens.</p>
      </Card>
    );
  }

  return (
    <div className="flex flex-col gap-5">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <Eyebrow>Lançamento · Mensal</Eyebrow>
          <h1 className="font-display text-[34px] md:text-[42px] font-bold tracking-[-0.03em] text-fg leading-[1.0] mt-1">
            Bens
          </h1>
          <p className="text-sm text-fg-muted mt-2">
            Móveis, imóveis e veículos vinculados ao convênio.
          </p>
        </div>
        <Button variant="primary" size="lg" onClick={() => setDrawerOpen(true)}>
          <Plus className="h-4 w-4" strokeWidth={1.8} />
          Novo bem
        </Button>
      </header>

      <section className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <KPI
          tone="mint"
          label="Patrimônio total"
          value={fmtBRL(aggQ.data?.total ?? '0')}
          delta={`${aggQ.data?.count ?? 0} ${(aggQ.data?.count ?? 0) === 1 ? 'item' : 'itens'}`}
          icon={Box}
        />
        <KPI
          label="Móveis"
          value={String(aggQ.data?.buckets.MOVEL.count ?? 0)}
          delta={fmtBRL(aggQ.data?.buckets.MOVEL.total ?? '0')}
          icon={Box}
        />
        <KPI
          label="Imóveis"
          value={String(aggQ.data?.buckets.IMOVEL.count ?? 0)}
          delta={fmtBRL(aggQ.data?.buckets.IMOVEL.total ?? '0')}
          icon={Building}
        />
        <KPI
          tone="ink"
          label="Veículos"
          value={String(aggQ.data?.buckets.VEICULO.count ?? 0)}
          delta={fmtBRL(aggQ.data?.buckets.VEICULO.total ?? '0')}
          icon={Car}
        />
      </section>

      <Card variant="default" className="p-4">
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative flex-1 min-w-[200px] max-w-md">
            <Search
              className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-fg-muted"
              strokeWidth={1.8}
            />
            <Input
              placeholder="Buscar por descrição…"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setOffset(0);
              }}
              className="pl-9"
            />
          </div>
          <Select
            value={tipo}
            onValueChange={(v) => {
              setTipo(v as BemTipo | 'ALL');
              setOffset(0);
            }}
          >
            <SelectTrigger className="w-[160px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="ALL">Todos os tipos</SelectItem>
              <SelectItem value="MOVEL">Móvel</SelectItem>
              <SelectItem value="IMOVEL">Imóvel</SelectItem>
              <SelectItem value="VEICULO">Veículo</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </Card>

      <div className="rounded-3 border border-card-stroke bg-card shadow-1 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-card-stroke bg-card-soft/60">
                <th className="px-4 py-3 text-left font-mono text-[10px] uppercase tracking-[0.10em] text-fg-muted">Aquisição</th>
                <th className="px-4 py-3 text-left font-mono text-[10px] uppercase tracking-[0.10em] text-fg-muted">Descrição</th>
                <th className="px-4 py-3 text-left font-mono text-[10px] uppercase tracking-[0.10em] text-fg-muted">Tipo</th>
                <th className="px-4 py-3 text-left font-mono text-[10px] uppercase tracking-[0.10em] text-fg-muted">Estado</th>
                <th className="px-4 py-3 text-right font-mono text-[10px] uppercase tracking-[0.10em] text-fg-muted">Valor</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody>
              {listQ.isLoading && (
                <tr>
                  <td colSpan={6} className="px-4 py-10 text-center text-fg-muted">
                    <Loader2 className="inline h-4 w-4 animate-spin mr-2" strokeWidth={1.8} />
                    Carregando…
                  </td>
                </tr>
              )}
              {!listQ.isLoading && items.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-4 py-10 text-center text-fg-muted text-sm">
                    Nenhum bem cadastrado.
                  </td>
                </tr>
              )}
              {items.map((b) => (
                <tr
                  key={b.id}
                  className="border-b border-card-stroke/50 last:border-b-0 hover:bg-card-soft/40"
                >
                  <td className="px-4 py-3 text-sm text-fg whitespace-nowrap">
                    {fmtDate(b.dataAquisicao)}
                  </td>
                  <td className="px-4 py-3 text-sm font-medium text-fg">{b.descricao}</td>
                  <td className="px-4 py-3">
                    <Pill tone="default">{TIPO_LABEL[b.tipo]}</Pill>
                  </td>
                  <td className="px-4 py-3 text-sm text-fg-muted">{b.estado ?? '—'}</td>
                  <td className="px-4 py-3 text-right font-mono text-sm font-semibold text-fg tabular-nums">
                    {fmtBRL(b.valor)}
                  </td>
                  <td className="px-4 py-3 text-right relative">
                    <button
                      type="button"
                      onClick={() => setOpenRow(openRow === b.id ? null : b.id)}
                      className="grid h-8 w-8 ml-auto place-items-center rounded-1 text-fg-muted hover:text-fg hover:bg-card-soft"
                      aria-label="Ações"
                    >
                      <MoreHorizontal className="h-4 w-4" strokeWidth={1.8} />
                    </button>
                    {openRow === b.id && (
                      <>
                        <div
                          className="fixed inset-0 z-30"
                          onClick={() => setOpenRow(null)}
                          aria-hidden
                        />
                        <div className="absolute right-2 top-10 z-40 w-40 rounded-2 border border-card-stroke bg-card shadow-clay p-1">
                          <button
                            type="button"
                            onClick={() => {
                              setOpenRow(null);
                              setConfirmDelete(b);
                            }}
                            className="flex w-full items-center gap-2 rounded-1 px-2 py-1.5 text-sm text-warn hover:bg-warn-soft"
                          >
                            <Trash2 className="h-4 w-4" strokeWidth={1.8} />
                            Excluir
                          </button>
                        </div>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {total > 0 && (
          <div className="flex items-center justify-between gap-2 border-t border-card-stroke px-4 py-3 bg-card-soft/40">
            <p className="text-xs text-fg-muted">
              Página <span className="text-fg font-medium">{currentPage}</span> de{' '}
              <span className="text-fg font-medium">{totalPages}</span> · {total}{' '}
              {total === 1 ? 'item' : 'itens'}
            </p>
            <div className="flex items-center gap-1">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                disabled={!canPrev}
                onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
              >
                <ChevronLeft className="h-4 w-4" strokeWidth={1.8} />
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                disabled={!canNext}
                onClick={() => setOffset(offset + PAGE_SIZE)}
              >
                <ChevronRight className="h-4 w-4" strokeWidth={1.8} />
              </Button>
            </div>
          </div>
        )}
      </div>

      <BemDrawer workspaceId={workspaceId} open={drawerOpen} onOpenChange={setDrawerOpen} />

      <ConfirmDialog
        open={!!confirmDelete}
        onOpenChange={(v) => !v && setConfirmDelete(null)}
        tone="danger"
        title="Excluir este bem?"
        description={
          confirmDelete
            ? `${confirmDelete.descricao} · ${fmtBRL(confirmDelete.valor)}. Não dá pra desfazer.`
            : undefined
        }
        confirmLabel="Excluir"
        loading={delMut.isPending}
        onConfirm={handleDelete}
      />
    </div>
  );
}
